<template>
  <div class="exceptions white-text">
    <p class="Heebo hint">
      ✓ יש חלוקה, ✗ אין חלוקה. תא מסומן במסגרת = יום חלוקה קבוע שנסגר.
      ניתן לשנות רק ימי חלוקה קבועים של הקו. לחיצה על תא משנה את היום לקו אחד,
      לחיצה על כותרת היום משנה לכל הקווים.
      תא במסגרת מקווקוות = שינוי שעוד לא נשמר. השינויים נשמרים רק בלחיצה
      על "שמירה", ובסגירת יום תוכלו לבחור אם לשלוח הודעה ללקוחות הקו.
    </p>
    <CartModal
      v-if="isNotifyModalOpen"
      icon="undraw_exclamation-point.png"
      titleOne="לשלוח הודעה ללקוחות?"
      :titleTwo="`${closingText}. לשלוח ללקוחות הקווים הודעת ווטסאפ ומייל על השינוי?`"
      confirmText="כן, לשלוח הודעה"
      rejectText="לא, לשמור בלי הודעה"
      dismissText="סגירה ללא שמירת השינויים"
      @exit-modal="onNotifyAnswer"
    />
    <div class="save-bar flex gap-10 Heebo">
      <input
        v-model="note"
        class="note-input"
        placeholder="סיבת הסגירה (לא חובה, תופיע בהודעה ללקוחות)"
      />
      <BaseButton @click="save" :isDisabled="!pendingCount || isSaving">
        שמירה{{ pendingCount ? ` (${pendingCount} שינויים)` : "" }}
      </BaseButton>
      <BaseButton
        v-if="pendingCount"
        @click="pending = {}"
        :bgColor="'#393945'"
      >
        ביטול שינויים
      </BaseButton>
    </div>
    <div class="week-nav flex gap-10">
      <BaseButton @click="weekOffset--" :isDisabled="weekOffset === 0">
        שבוע קודם
      </BaseButton>
      <span class="Heebo week-label">{{ weekLabel }}</span>
      <BaseButton @click="weekOffset++">שבוע הבא</BaseButton>
    </div>
    <div class="table-wrap">
      <table class="Heebo">
        <thead>
          <tr>
            <th>קו חלוקה</th>
            <th v-for="day in weekDays" :key="day.key">
              <button
                class="day-header"
                :disabled="day.isPast"
                @click="toggleAllLines(day)"
              >
                {{ day.label }}<br />{{ day.dateText }}
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="line in getDistLines" :key="line.linekey">
            <td class="line-name">{{ line.linekey }} - {{ line.linedes }}</td>
            <td
              v-for="day in weekDays"
              :key="day.key"
              :title="cellTitle(line, day)"
              :class="{ 'readonly-cell': !isRegular(line, day) }"
            >
              <button
                class="cell"
                :class="{
                  open: isOpen(line, day),
                  exception: isRegular(line, day) && !isOpen(line, day),
                  pending: isPending(line, day),
                  readonly: !isRegular(line, day),
                }"
                :disabled="day.isPast || !isRegular(line, day)"
                @click="setPending(line, day, !isOpen(line, day))"
              >
                {{ isOpen(line, day) ? "✓" : "✗" }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import BaseButton from "../helpers/BaseButton.vue";
import CartModal from "../helpers/CartModal.vue";
import { isDeliveryDay, toDateKey } from "../../Dates";

const DAY_LABELS = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי"];

export default {
  components: { BaseButton, CartModal },
  data() {
    // pending: unsaved changes, "linekey|date" -> isOpen
    return {
      weekOffset: 0,
      pending: {},
      note: "",
      isSaving: false,
      isNotifyModalOpen: false,
    };
  },
  mounted() {
    this.$store.dispatch("DistLines/fetchExceptions");
  },
  beforeRouteLeave() {
    if (!this.pendingCount) return true;
    return window.confirm("יש שינויים שלא נשמרו. לצאת בלי לשמור?");
  },
  computed: {
    ...mapGetters({
      getDistLines: "DistLines/getDistLines",
      getExceptions: "DistLines/getExceptions",
    }),
    weekDays() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const sunday = new Date(today);
      sunday.setDate(today.getDate() - today.getDay() + this.weekOffset * 7);
      return DAY_LABELS.map((label, i) => {
        const date = new Date(sunday);
        date.setDate(sunday.getDate() + i);
        return {
          key: toDateKey(date),
          date,
          label,
          dateText: `${date.getDate()}/${date.getMonth() + 1}`,
          isPast: date < today,
        };
      });
    },
    weekLabel() {
      return `${this.weekDays[0].dateText} - ${this.weekDays[5].dateText}`;
    },
    pendingCount() {
      return Object.keys(this.pending).length;
    },
    closingCount() {
      return Object.values(this.pending).filter((isOpen) => !isOpen).length;
    },
    closingText() {
      return this.closingCount === 1
        ? "נסגר יום חלוקה אחד"
        : `נסגרו ${this.closingCount} ימי חלוקה`;
    },
  },
  methods: {
    exceptionOf(line, day) {
      return this.getExceptions.find(
        (e) => e.linekey === line.linekey && e.date === day.key
      );
    },
    pendingKey(line, day) {
      return `${line.linekey}|${day.key}`;
    },
    isRegular(line, day) {
      return line[`day${day.date.getDay() + 1}`] === true;
    },
    cellTitle(line, day) {
      if (!this.isRegular(line, day)) {
        return `יום ${day.label} אינו יום חלוקה של קו ${line.linekey}. כדי לשנות אותו יש להפעיל קודם את היום בקו במסך "עריכת קווי חלוקה"`;
      }
      return this.exceptionOf(line, day)?.note || "";
    },
    isSaved(line, day) {
      const lineExceptions = this.getExceptions.filter(
        (e) => e.linekey === line.linekey
      );
      return isDeliveryDay(line, lineExceptions, day.date);
    },
    isPending(line, day) {
      return this.pendingKey(line, day) in this.pending;
    },
    isOpen(line, day) {
      const key = this.pendingKey(line, day);
      return key in this.pending ? this.pending[key] : this.isSaved(line, day);
    },
    setPending(line, day, isOpen) {
      const key = this.pendingKey(line, day);
      if (isOpen === this.isSaved(line, day)) delete this.pending[key];
      else this.pending[key] = isOpen;
    },
    toggleAllLines(day) {
      const regularLines = this.getDistLines.filter((l) => this.isRegular(l, day));
      // any line delivering -> close the day for all of them; all closed -> reopen them
      const anyOpen = regularLines.some((l) => this.isOpen(l, day));
      for (const line of regularLines) this.setPending(line, day, !anyOpen);
    },
    save() {
      // closures ask whether to message customers; reopen-only saves go straight through
      if (this.closingCount) this.isNotifyModalOpen = true;
      else this.submit(false);
    },
    onNotifyAnswer({ isConfirmed, isDismissed }) {
      this.isNotifyModalOpen = false;
      // dismissed: nothing is saved, the pending changes stay on screen
      if (!isDismissed) this.submit(isConfirmed);
    },
    async submit(notify) {
      const changes = Object.entries(this.pending).map(([key, isOpen]) => {
        const [linekey, date] = key.split("|");
        return { linekey: Number(linekey), date, isOpen };
      });
      this.isSaving = true;
      const saved = await this.$store.dispatch("DistLines/saveDayChanges", {
        changes,
        note: this.note.trim() || null,
        notify,
      });
      this.isSaving = false;
      if (saved) {
        this.pending = {};
        this.note = "";
      }
    },
  },
};
</script>

<style scoped lang="scss">
.exceptions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  max-width: 900px;
  margin: auto;
}
.hint {
  text-align: center;
  font-size: 0.95rem;
}
.week-nav {
  justify-content: center;
  align-items: center;
}
.week-label {
  font-size: 1.2rem;
}
.table-wrap {
  overflow-x: auto;
  padding-bottom: 60px;
}
table {
  width: 100%;
  border-collapse: collapse;
  text-align: center;
}
th,
td {
  padding: 4px;
  text-align: right;
  border-bottom: 1px solid #393945;
}
.line-name {
  text-align: right;
  white-space: nowrap;
}
.day-header {
  background: none;
  border: none;
  color: $primaryTextColor;
  font-size: 1rem;
  cursor: pointer;
}
.cell {
  width: 40px;
  height: 36px;
  border-radius: 10px;
  border: 2px solid transparent;
  background: #393945;
  color: #c4c4c4;
  font-size: 1.2rem;
  cursor: pointer;
}
.cell.open {
  background: #1f5e2a;
  color: white;
}
.cell.exception {
  border-color: $primaryTextColor;
}
.cell.readonly {
  background: transparent;
  color: #5a5a66;
  // let the hover reach the td's title; disabled buttons don't show tooltips in every browser
  pointer-events: none;
}
td.readonly-cell {
  cursor: not-allowed;
}
.cell.pending {
  border-style: dashed;
  box-shadow: 0 0 6px $primaryTextColor;
}
.save-bar {
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}
.note-input {
  color: white;
  text-align: center;
  max-width: 360px;
}
button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
